import { type DefaultContext, mutationPromise } from '@egw/clients'
import {
  type EnrollmentFilingStatusData,
  type EnrollmentFilingStatusSubmitInput,
  type EnrollmentFilingStatusSubmitOutput,
} from '../../../models'
import { createMutationHook, createQueryHook } from '../../../hooks'

/**
 * Get Enrollment FilingStatus data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentFilingStatusData>
 * @example const response = await getEnrollmentFilingStatusData()
 */
export const getEnrollmentFilingStatusData = async (context?: DefaultContext): Promise<EnrollmentFilingStatusData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentFilingStatusData',
      query: `mutation getEnrollmentFilingStatusData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
            enrollmentData {
              userInfo {
                income
                taxFilingStatus
              }
            }
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: {},
    },
    context,
  )) as { submitEnrollment: EnrollmentFilingStatusData }
  return response.submitEnrollment
}

export const useGetEnrollmentFilingStatusData = createQueryHook<undefined, EnrollmentFilingStatusData>(
  getEnrollmentFilingStatusData,
)

/**
 * Submit Enrollment FilingStatus data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentFilingStatusSubmitOutput>
 * @example const response = await submitEnrollmentFilingStatusData();
 */
export const submitEnrollmentFilingStatusData = async (
  enrollmentFilingStatusSubmitInput: EnrollmentFilingStatusSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentFilingStatusSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentFilingStatusData',
      query: `mutation submitEnrollmentFilingStatusData($submitEnrollmentInput: SubmitEnrollmentInput!) {
        submitEnrollment(submitEnrollmentInput: $submitEnrollmentInput) {
          ... on SubmitEnrollmentSuccess {
            __typename
            path
          }
          ... on SubmitEnrollmentError {
            __typename
            path
          }
        }
      }`,
    },
    {
      submitEnrollmentInput: enrollmentFilingStatusSubmitInput,
    },
    context,
  )) as { submitEnrollment: EnrollmentFilingStatusSubmitOutput }
  return response.submitEnrollment
}

export const useSubmitEnrollmentFilingStatusData = createMutationHook<
  EnrollmentFilingStatusSubmitInput,
  EnrollmentFilingStatusSubmitOutput
>(submitEnrollmentFilingStatusData)
